import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { io, type Socket } from 'socket.io-client';
import { SOCKET_EVENTS, type MaintenanceNoticeEvent } from '@atlas/shared';
import { useAuth } from './AuthContext';
import { setMaintenanceNotice } from '@/lib/maintenance';

// Socket server URL - use relative path in production, absolute in development
const getSocketUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  
  // If API URL starts with /, it's a relative path (production)
  if (apiUrl && apiUrl.startsWith('/')) {
    // Use current window location for WebSocket connection
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
  }
  
  // Otherwise use the configured URL or fallback to localhost (development)
  return apiUrl?.replace('/api/v1', '') || 'http://localhost:3000';
};

const SOCKET_URL = getSocketUrl();

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface SocketContextType {
  socket: Socket | null;
  connectionState: ConnectionState;
  socketId: string | null;
  connect: () => void;
  disconnect: () => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export function SocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [socketId, setSocketId] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    // Don't connect if not authenticated
    if (!isAuthenticated) {
      return;
    }

    // Don't create duplicate connections
    if (socketRef.current?.connected) {
      return;
    }

    // Clean up existing socket
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setConnectionState('connecting');

    const newSocket = io(SOCKET_URL, {
      withCredentials: true, // Send httpOnly cookies with the WebSocket upgrade request
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    // Connection events
    newSocket.on('connect', () => {
      setConnectionState('connected');
      setSocketId(newSocket.id || null);
      reconnectAttempts.current = 0;
    });

    newSocket.on('disconnect', (reason) => {
      setConnectionState('disconnected');
      setSocketId(null);

      // If server disconnected us, try to reconnect
      if (reason === 'io server disconnect') {
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      reconnectAttempts.current++;

      if (reconnectAttempts.current >= maxReconnectAttempts) {
        setConnectionState('error');
      }
    });

    newSocket.on('reconnect', (_attemptNumber) => {
      setConnectionState('connected');
      reconnectAttempts.current = 0;
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error.message);
    });

    // The deploy is about to swap the containers. Raise the banner and, through
    // it, ask any open form to flush what it has — the alternative is a request
    // that dies mid-restart with the user none the wiser.
    // A null payload withdraws a notice that is still on screen — a deploy that
    // aborted, or a test. Handled by passing it straight through, which is also
    // why an already-loaded page can be cleared without shipping it new code.
    newSocket.on(
      SOCKET_EVENTS.SYSTEM_MAINTENANCE,
      (notice: MaintenanceNoticeEvent | null) => setMaintenanceNotice(notice),
    );

    newSocket.on('reconnect_failed', () => {
      console.error('Socket reconnection failed');
      setConnectionState('error');
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  }, [isAuthenticated]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setConnectionState('disconnected');
      setSocketId(null);
    }
  }, []);

  // Auto-connect when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, user, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        connectionState,
        socketId,
        connect,
        disconnect,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}
